import { useEffect, useState, type ReactElement } from 'react';
import type {
  EnforceDisabledReason,
  PolicyModeType,
} from '@kirin/api-client/contracts/policies';
import type { AllowBlockPolicyKey, AllowBlockState } from '../types';
import { AllowBlockSection } from './AllowBlockSection';

export interface AllowBlockPolicyEditorProps {
  policyKey: AllowBlockPolicyKey;
  value: AllowBlockState;
  onChange: (next: AllowBlockState) => void;
  /** Last saved baseline (for diff highlights + pending delete). */
  savedBaseline: AllowBlockState;
  readOnly: boolean;
  /** Support role: rows stay viewable/expandable, only commit actions are disabled. */
  isSupportReadOnly?: boolean;
  supportsEnforce?: boolean;
  enforceDisabledReason?: EnforceDisabledReason;
  enforceTooltip?: string;
  /** Allowed + blocked tables (including drafts) — gates auto-save while invalid. */
  onFormValidityChange?: (valid: boolean) => void;
}

export function AllowBlockPolicyEditor({
  policyKey,
  value,
  onChange,
  savedBaseline,
  readOnly,
  isSupportReadOnly = false,
  supportsEnforce = false,
  enforceDisabledReason,
  enforceTooltip,
  onFormValidityChange,
}: AllowBlockPolicyEditorProps): ReactElement {
  const [allowedListValid, setAllowedListValid] = useState(true);
  const [blockedListValid, setBlockedListValid] = useState(true);

  useEffect(() => {
    onFormValidityChange?.(allowedListValid && blockedListValid);
  }, [allowedListValid, blockedListValid, onFormValidityChange]);

  const setMode = (blockedListMode: PolicyModeType): void => {
    onChange({ ...value, blockedListMode });
  };

  const blockedModeDirty =
    value.blockedListMode !== savedBaseline.blockedListMode;

  return (
    <div className="space-y-6">
      <AllowBlockSection
        policyKey={policyKey}
        variant="allowed"
        rules={value.allowed}
        baselineRules={savedBaseline.allowed}
        onRulesChange={allowed => onChange({ ...value, allowed })}
        readOnly={readOnly}
        isSupportReadOnly={isSupportReadOnly}
        onRuleListValidationChange={setAllowedListValid}
      />
      <AllowBlockSection
        policyKey={policyKey}
        variant="blocked"
        rules={value.blocked}
        baselineRules={savedBaseline.blocked}
        onRulesChange={blocked => onChange({ ...value, blocked })}
        readOnly={readOnly}
        isSupportReadOnly={isSupportReadOnly}
        blockedListMode={value.blockedListMode}
        onBlockedListModeChange={setMode}
        blockedModeDirty={blockedModeDirty}
        supportsEnforce={supportsEnforce}
        enforceDisabledReason={enforceDisabledReason}
        enforceTooltip={enforceTooltip}
        onRuleListValidationChange={setBlockedListValid}
      />
    </div>
  );
}
